<template>
    <Card pageTitle="Cova Sync">
        <AllowPermission :permission="PERMISSION_CODES.CMS_COVA_STOCK_IMPORT">
            <div class="cova-sync-page">
                <header class="sync-hero">
                    <div>
                        <div class="eyebrow">One place for every Cova export</div>
                        <h2>Upload the files. We’ll figure out the rest.</h2>
                        <p>Cova Sync identifies each report, explains what it can safely do, and guides you to the right next step.</p>
                    </div>
                    <span class="safety-pill"><span class="material-icons">verified_user</span>Nothing changes during analysis</span>
                </header>

                <section class="sync-card upload-card">
                    <div class="section-heading">
                        <div>
                            <span class="step-label">Step 1</span>
                            <h3>Add your Cova files</h3>
                            <p>Choose one or several Excel or CSV exports. You do not need to choose an import type.</p>
                        </div>
                        <span v-if="files.length" class="count-pill">{{ files.length }} file{{ files.length === 1 ? "" : "s" }}</span>
                    </div>

                    <div
                        class="file-drop"
                        :class="{ dragging: isDragging, disabled: busy }"
                        role="button"
                        tabindex="0"
                        @click="openFilePicker"
                        @keydown.enter.prevent="openFilePicker"
                        @keydown.space.prevent="openFilePicker"
                        @dragenter.prevent="isDragging = true"
                        @dragover.prevent="isDragging = true"
                        @dragleave.prevent="isDragging = false"
                        @drop.prevent="handleDrop"
                    >
                        <span class="material-icons upload-icon">upload_file</span>
                        <strong>Drop Cova spreadsheets here</strong>
                        <span>or click to browse</span>
                        <small>.xlsx, .xls, and .csv</small>
                        <input
                            ref="fileInput"
                            class="file-input"
                            type="file"
                            multiple
                            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                            :disabled="busy"
                            @change="handleFilePicker"
                        />
                    </div>

                    <div v-if="files.length" class="selected-files">
                        <div v-for="file in files" :key="fileKey(file)" class="selected-file">
                            <span class="material-icons">description</span>
                            <div>
                                <strong>{{ file.name }}</strong>
                                <small>{{ fileSize(file.size) }}</small>
                            </div>
                            <button type="button" :disabled="busy" aria-label="Remove file" @click="removeFile(file)">
                                <span class="material-icons">close</span>
                            </button>
                        </div>
                    </div>

                    <div class="action-row">
                        <Button type="button" :disabled="!files.length || busy" @click="analyzeFiles">
                            {{ analyzing ? "Reading Cova files…" : analysis ? "Analyze again" : "Analyze files" }}
                        </Button>
                        <button v-if="files.length" class="text-button" type="button" :disabled="busy" @click="resetAll">Start over</button>
                    </div>
                </section>

                <UiState v-if="analyzing" type="loading" title="Identifying your Cova reports" message="Checking fields, products, prices, quantities, and package information." />

                <template v-if="analysis && !analyzing">
                    <section class="sync-card">
                        <div class="section-heading">
                            <div>
                                <span class="step-label">Step 2</span>
                                <h3>Here’s what we found</h3>
                                <p>{{ analysis.summary.recognizedFiles }} of {{ analysis.summary.uploadedFiles }} files were recognized.</p>
                            </div>
                            <span class="count-pill ready">{{ numberFormat(analysis.summary.uniqueSkus) }} SKUs</span>
                        </div>

                        <div class="detected-files">
                            <article v-for="file in analysis.files" :key="file.uploadIndex" class="detected-file" :class="{ unknown: !file.recognized }">
                                <span class="material-icons">{{ file.recognized ? "task_alt" : "help_outline" }}</span>
                                <div>
                                    <strong>{{ file.fileName }}</strong>
                                    <p>{{ fileDescription(file) }}</p>
                                </div>
                                <span class="status-chip" :class="file.recognized ? 'good' : 'warning'">
                                    {{ file.recognized ? "Recognized" : "Unknown format" }}
                                </span>
                            </article>
                        </div>
                    </section>

                    <section v-if="hasCompatibilityWarnings" class="sync-card compatibility-review" aria-labelledby="compatibility-heading">
                        <div class="section-heading">
                            <div>
                                <span class="step-label">File check</span>
                                <h3 id="compatibility-heading">These files need a little attention</h3>
                                <p>We compared the reports with each other before enabling any actions.</p>
                            </div>
                        </div>

                        <div v-if="priceDisagreementCount" class="notice info-notice">
                            <span class="material-icons">price_check</span>
                            <p>
                                {{ priceDisagreementCount }} price{{ priceDisagreementCount === 1 ? " differs" : "s differ" }} between the reports.
                                Inventory On Hand is store-specific, so its price will be used. Product Catalog is still used for product details.
                            </p>
                        </div>

                        <div v-if="missingPackageCatalogCount" class="notice warning-notice">
                            <span class="material-icons">warning_amber</span>
                            <p>
                                {{ missingPackageCatalogCount }} package SKU{{ missingPackageCatalogCount === 1 ? " is" : "s are" }} missing from Product Catalog.
                                Guided receiving is paused. Export a current Product Catalog from Cova and analyze these files again.
                            </p>
                        </div>
                    </section>

                    <section class="sync-card">
                        <div class="section-heading">
                            <div>
                                <span class="step-label">Step 3</span>
                                <h3>What these files can do</h3>
                                <p>Available actions are based on the actual fields found in the uploaded reports.</p>
                            </div>
                        </div>

                        <div class="capability-grid">
                            <article v-for="item in analysis.capabilities" :key="item.key" class="capability" :class="capabilityClass(item)">
                                <div class="capability-icon"><span class="material-icons">{{ capabilityIcon(item) }}</span></div>
                                <div class="capability-copy">
                                    <div class="capability-title">
                                        <h4>{{ item.title }}</h4>
                                        <span class="status-chip" :class="capabilityStatusClass(item)">{{ capabilityStatus(item) }}</span>
                                    </div>
                                    <p>{{ item.description }}</p>
                                    <ul v-if="item.missing && item.missing.length">
                                        <li v-for="missing in item.missing" :key="missing">Add: {{ missing }}</li>
                                    </ul>
                                    <button
                                        v-if="item.key === 'product_create' && item.available"
                                        class="inline-action"
                                        type="button"
                                        @click="continueToProductSetup"
                                    >
                                        Continue to product setup <span class="material-icons">arrow_forward</span>
                                    </button>
                                    <button
                                        v-if="item.key === 'package_receive' && item.available"
                                        class="inline-action"
                                        type="button"
                                        @click="continueToReceiving"
                                    >
                                        Continue to guided receiving <span class="material-icons">arrow_forward</span>
                                    </button>
                                    <button
                                        v-if="item.key === 'package_reconcile' && item.available"
                                        class="inline-action"
                                        type="button"
                                        @click="continueToReconciliation"
                                    >
                                        Continue to package reconciliation <span class="material-icons">arrow_forward</span>
                                    </button>
                                </div>
                            </article>
                        </div>
                    </section>

                    <section v-if="priceCapability && priceCapability.available" class="sync-card price-review">
                        <div class="section-heading">
                            <div>
                                <span class="step-label">Price review</span>
                                <h3 v-if="analysis.priceSummary.changes">
                                    {{ analysis.priceSummary.changes }} existing price{{ analysis.priceSummary.changes === 1 ? "" : "s" }} ready to sync
                                </h3>
                                <h3 v-else-if="priceIssueCount">Prices need review</h3>
                                <h3 v-else>Existing prices are up to date</h3>
                                <p>Only existing products are updated. Inventory On Hand prices take priority when included. Unmatched SKUs remain untouched.</p>
                            </div>
                            <div class="price-stats">
                                <span><strong>{{ analysis.priceSummary.unchanged }}</strong> already match</span>
                                <span><strong>{{ analysis.priceSummary.unmatched }}</strong> new or unmatched</span>
                            </div>
                        </div>

                        <div v-if="priceIssueCount" class="notice warning-notice">
                            <span class="material-icons">warning_amber</span>
                            <p>{{ priceIssueCount }} price row{{ priceIssueCount === 1 ? " needs" : "s need" }} attention and will not be changed.</p>
                        </div>

                        <div v-if="remainingUnreviewedPriceCount" class="notice info-notice">
                            <span class="material-icons">fact_check</span>
                            <p>
                                This batch shows {{ reviewedPriceCount }} of {{ analysis.priceSummary.changes }} price changes. Sync only this reviewed batch, then analyze again for the next batch.
                            </p>
                        </div>

                        <div class="table-wrap">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Product</th>
                                        <th>SKU</th>
                                        <th>High Ties</th>
                                        <th>Cova</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="row in visiblePriceRows" :key="row.sku">
                                        <td>
                                            <strong>{{ row.productName || "Unnamed product" }}</strong>
                                            <small>{{ priceSourceLabel(row) }}</small>
                                        </td>
                                        <td>{{ row.sku }}</td>
                                        <td>{{ money(highTiesPrice(row)) }}</td>
                                        <td>{{ money(row.covaPrice) }}</td>
                                        <td><span class="status-chip" :class="priceStatusClass(row)">{{ priceStatus(row) }}</span></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div v-if="analysis.priceSummary.changes" class="confirmation-panel">
                            <label>
                                <input v-model="confirmPrices" type="checkbox" :disabled="busy" />
                                <span>I reviewed the price changes. Update these existing products to the Cova regular prices.</span>
                            </label>
                            <Button type="button" :disabled="!confirmPrices || busy || !reviewedPriceCount" @click="syncPrices">
                                {{ applyingPrices ? "Syncing prices…" : `Sync ${reviewedPriceCount} reviewed price${reviewedPriceCount === 1 ? "" : "s"}` }}
                            </Button>
                        </div>

                        <div v-if="priceResult" class="notice success-notice">
                            <span class="material-icons">check_circle</span>
                            <p><strong>Price sync complete.</strong> {{ priceResult.summary.applied }} existing product prices were updated.</p>
                        </div>
                    </section>

                    <section v-if="inventoryCapability && inventoryCapability.available" class="sync-card inventory-review">
                        <div class="section-heading inventory-heading">
                            <div>
                                <span class="step-label">Inventory comparison</span>
                                <h3>Compare totals without changing stock</h3>
                                <p>This report has no package IDs, so quantity differences remain review-only.</p>
                            </div>
                            <div class="location-field">
                                <label for="warehouseId">High Ties location</label>
                                <select id="warehouseId" v-model="warehouseId" :disabled="busy" @change="analyzeFiles">
                                    <option value="">Choose location</option>
                                    <option v-for="warehouse in warehouses" :key="warehouse.id" :value="warehouse.id">{{ warehouse.name }}</option>
                                </select>
                            </div>
                        </div>

                        <div v-if="analysis.inventoryComparison.status === 'needs_location'" class="notice info-notice">
                            <span class="material-icons">location_on</span>
                            <p>Choose the matching High Ties location to compare the Cova snapshot.</p>
                        </div>
                        <div v-else-if="['multiple_locations', 'multiple_files', 'invalid_warehouse'].includes(analysis.inventoryComparison.status)" class="notice warning-notice">
                            <span class="material-icons">warning_amber</span>
                            <p>{{ analysis.inventoryComparison.message }}</p>
                        </div>
                        <template v-else-if="analysis.inventoryComparison.summary">
                            <div class="metric-row">
                                <div><span>Compared</span><strong>{{ numberFormat(analysis.inventoryComparison.summary.comparedRows) }}</strong></div>
                                <div><span>Different</span><strong>{{ numberFormat(analysis.inventoryComparison.summary.changedRows) }}</strong></div>
                                <div><span>Already match</span><strong>{{ numberFormat(analysis.inventoryComparison.summary.unchangedRows) }}</strong></div>
                                <div><span>Unmatched</span><strong>{{ numberFormat(analysis.inventoryComparison.summary.unmatchedRows) }}</strong></div>
                                <div v-if="analysis.inventoryComparison.summary.ambiguousRows">
                                    <span>Ambiguous</span><strong>{{ numberFormat(analysis.inventoryComparison.summary.ambiguousRows) }}</strong>
                                </div>
                                <div v-if="analysis.inventoryComparison.summary.invalidQuantityRows">
                                    <span>Invalid quantity</span><strong>{{ numberFormat(analysis.inventoryComparison.summary.invalidQuantityRows) }}</strong>
                                </div>
                                <div v-if="analysis.inventoryComparison.summary.customRoomRows">
                                    <span>Custom room</span><strong>{{ numberFormat(analysis.inventoryComparison.summary.customRoomRows) }}</strong>
                                </div>
                                <div v-if="analysis.inventoryComparison.summary.localOnlyRows">
                                    <span>High Ties only</span><strong>{{ numberFormat(analysis.inventoryComparison.summary.localOnlyRows) }}</strong>
                                </div>
                                <div v-if="analysis.inventoryComparison.summary.unsupportedRoomRows">
                                    <span>Unknown room</span><strong>{{ numberFormat(analysis.inventoryComparison.summary.unsupportedRoomRows) }}</strong>
                                </div>
                            </div>
                            <div class="table-wrap compact-table">
                                <table>
                                    <thead><tr><th>Product</th><th>Room</th><th>Cova</th><th>High Ties</th><th>Difference</th></tr></thead>
                                    <tbody>
                                        <tr v-for="row in visibleInventoryRows" :key="`${row.sku}-${row.roomName}`">
                                            <td><strong>{{ row.productName }}</strong><small>{{ row.sku }}</small></td>
                                            <td>{{ row.roomName }}</td>
                                            <td>{{ row.status === "invalid_quantity" ? "Invalid" : row.covaQty === null ? "—" : numberFormat(row.covaQty) }}</td>
                                            <td>{{ ["unmatched", "ambiguous", "custom_room_stock", "unsupported_room"].includes(row.status) ? "—" : numberFormat(row.highTiesQty) }}</td>
                                            <td :class="deltaClass(row.delta)">
                                                {{ ["invalid_quantity", "ambiguous", "custom_room_stock", "unsupported_room"].includes(row.status) ? "Needs review" : row.status === "unmatched" ? "Not matched" : signedNumber(row.delta) }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div v-if="remainingInventoryRows" class="show-more-row">
                                <button class="text-button" type="button" @click="inventoryRowLimit += 100">
                                    Show 100 more differences ({{ numberFormat(remainingInventoryRows) }} remaining)
                                </button>
                            </div>
                        </template>
                    </section>
                </template>
            </div>
        </AllowPermission>
    </Card>
</template>

<script>
import { mapActions, mapState } from "vuex"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import Button from "../../components/UI/Button.vue"
import Card from "../../components/UI/Card.vue"
import UiState from "../../components/UI/UiState.vue"
import { PERMISSION_CODES } from "../../store/permission"
import { errMsg } from "../../utils/common"
import { http } from "../../utils/http"
import { setCovaSyncHandoff } from "../../utils/cova-sync-session"

const ROLE_LABELS = {
    inventory_on_hand: "Inventory On Hand with prices",
    product_catalog: "Product catalogue",
    package_inventory: "Package-level inventory",
}
const MAX_SYNC_FILES = 6
const MAX_SYNC_TOTAL_BYTES = 50 * 1024 * 1024

export default {
    name: "CovaSync",
    components: { AllowPermission, Button, Card, UiState },
    data() {
        return {
            PERMISSION_CODES,
            files: [],
            analysis: null,
            warehouseId: "",
            analyzing: false,
            applyingPrices: false,
            confirmPrices: false,
            priceResult: null,
            isDragging: false,
            inventoryRowLimit: 100,
        }
    },
    computed: {
        ...mapState("misc", ["warehouses"]),
        busy() {
            return this.analyzing || this.applyingPrices
        },
        priceCapability() {
            return this.analysis?.capabilities?.find((item) => item.key === "price_sync") || null
        },
        inventoryCapability() {
            return this.analysis?.capabilities?.find((item) => item.key === "inventory_compare") || null
        },
        visiblePriceRows() {
            return (this.analysis?.pricePreview || []).filter((row) => row.status !== "unchanged")
        },
        reviewedPrices() {
            return this.visiblePriceRows
                .filter((row) => row.status === "change")
                .map((row) => ({
                    sku: row.sku,
                    itemId: row.itemId,
                    itemCode: row.itemCode,
                    currentPrice: row.currentPrice,
                    covaPrice: row.covaPrice,
                }))
        },
        reviewedPriceCount() {
            return this.reviewedPrices.length
        },
        remainingUnreviewedPriceCount() {
            return Math.max(0, Number(this.analysis?.priceSummary?.changes || 0) - this.reviewedPriceCount)
        },
        priceIssueCount() {
            const summary = this.analysis?.priceSummary || {}
            return Number(summary.unmatched || 0) + Number(summary.ambiguous || 0) + Number(summary.missingPrice || 0) + Number(summary.conflicts || 0)
        },
        priceDisagreementCount() {
            return Number(this.analysis?.compatibility?.priceDisagreements?.count || 0)
        },
        missingPackageCatalogCount() {
            return Number(this.analysis?.compatibility?.packageCatalogCoverage?.missingCount || 0)
        },
        hasCompatibilityWarnings() {
            return this.priceDisagreementCount > 0 || this.missingPackageCatalogCount > 0
        },
        visibleInventoryRows() {
            return (this.analysis?.inventoryComparison?.rows || []).slice(0, this.inventoryRowLimit)
        },
        remainingInventoryRows() {
            return Math.max(0, Number(this.analysis?.inventoryComparison?.rows?.length || 0) - this.inventoryRowLimit)
        },
    },
    mounted() {
        this.setData({ pageTitle: "Cova Sync" })
    },
    methods: {
        ...mapActions("misc", ["setData"]),
        fileKey(file) {
            return `${file.name}-${file.size}-${file.lastModified}`
        },
        fileSize(bytes) {
            if (bytes < 1024) return `${bytes} B`
            if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
            return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
        },
        openFilePicker() {
            if (!this.busy) this.$refs.fileInput?.click()
        },
        handleFilePicker(event) {
            this.addFiles(Array.from(event.target.files || []))
            event.target.value = ""
        },
        handleDrop(event) {
            this.isDragging = false
            if (!this.busy) this.addFiles(Array.from(event.dataTransfer.files || []))
        },
        addFiles(newFiles) {
            const files = [...this.files]
            let exceededLimit = false
            newFiles.forEach((file) => {
                if (files.some((current) => this.fileKey(current) === this.fileKey(file))) return
                if (files.length >= MAX_SYNC_FILES) {
                    exceededLimit = true
                    return
                }
                if (files.reduce((total, current) => total + current.size, 0) + file.size > MAX_SYNC_TOTAL_BYTES) {
                    exceededLimit = true
                    return
                }
                files.push(file)
            })
            this.files = files
            this.clearAnalysis()
            if (exceededLimit) this.$toast.error(`Choose up to ${MAX_SYNC_FILES} files totaling no more than 50 MB.`)
        },
        removeFile(file) {
            this.files = this.files.filter((current) => this.fileKey(current) !== this.fileKey(file))
            this.clearAnalysis()
        },
        clearAnalysis() {
            this.analysis = null
            this.confirmPrices = false
            this.priceResult = null
            this.inventoryRowLimit = 100
        },
        resetAll() {
            this.files = []
            this.warehouseId = ""
            this.clearAnalysis()
        },
        buildFormData() {
            const formData = new FormData()
            this.files.forEach((file) => formData.append("files", file))
            if (this.warehouseId) formData.append("warehouseId", this.warehouseId)
            return formData
        },
        async analyzeFiles() {
            if (!this.files.length || this.busy) return
            this.analyzing = true
            this.priceResult = null
            let shouldRefreshForLocation = false
            try {
                const response = await http.post("/cova-sync/preflight", this.buildFormData(), { headers: { "Content-Type": "multipart/form-data" } })
                this.analysis = response.data.result
                this.confirmPrices = false
                this.inventoryRowLimit = 100
                shouldRefreshForLocation = this.autoMatchWarehouse()
                this.$toast.success("Cova files analyzed")
            } catch (err) {
                this.analysis = null
                this.$toast.error(errMsg(err, "Could not analyze the Cova files"))
            } finally {
                this.analyzing = false
            }
            if (shouldRefreshForLocation) await this.analyzeFiles()
        },
        autoMatchWarehouse() {
            if (this.warehouseId || this.analysis?.inventoryComparison?.detectedLocations?.length !== 1) return false
            const normalizeName = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim()
            const location = normalizeName(this.analysis.inventoryComparison.detectedLocations[0])
            const exactMatches = this.warehouses.filter((warehouse) => normalizeName(warehouse.name) === location)
            const match = exactMatches.length === 1 ? exactMatches[0] : null
            if (!match) return false
            this.warehouseId = match.id
            return true
        },
        async syncPrices() {
            if (!this.confirmPrices || this.busy) return
            this.applyingPrices = true
            try {
                const formData = this.buildFormData()
                formData.append("selectedPrices", JSON.stringify(this.reviewedPrices))
                const response = await http.post("/cova-sync/prices/apply", formData, { headers: { "Content-Type": "multipart/form-data" } })
                this.priceResult = response.data.result
                const appliedBySku = new Map(this.priceResult.applied.map((row) => [row.sku, row]))
                this.analysis.pricePreview = this.analysis.pricePreview.map((row) => {
                    const applied = appliedBySku.get(row.sku)
                    return applied
                        ? {
                              ...row,
                              currentPrice: applied.newPrice,
                              effectiveCurrentPrice: applied.newPrice,
                              status: "unchanged",
                              message: "Price already matches Cova.",
                          }
                        : row
                })
                this.analysis.priceSummary.changes -= this.priceResult.summary.applied
                this.analysis.priceSummary.unchanged += this.priceResult.summary.applied
                if (!this.analysis.priceSummary.changes && this.priceCapability) {
                    const unresolvedCount = this.priceIssueCount
                    this.priceCapability.status = unresolvedCount ? "needs_attention" : "up_to_date"
                    this.priceCapability.description = unresolvedCount
                        ? `${unresolvedCount} price row${unresolvedCount === 1 ? " still needs" : "s still need"} review and remains unchanged.`
                        : "The selected existing product prices now match Cova."
                }
                this.confirmPrices = false
                this.$toast.success(response.data.message)
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not synchronize Cova prices"))
            } finally {
                this.applyingPrices = false
            }
        },
        findSelectedFile(uploadIndex) {
            return Number.isInteger(uploadIndex) ? this.files[uploadIndex] || null : null
        },
        continueToProductSetup() {
            const productFile = this.findSelectedFile(this.analysis?.handoff?.productCatalogUploadIndex)
            setCovaSyncHandoff({ type: "import", productFile, stockFile: null, fileMode: "catalog-only" })
            this.$router.push("/cova-import/advanced")
        },
        continueToReceiving() {
            const receiving = this.analysis?.capabilities?.find((item) => item.key === "package_receive")
            if (!receiving?.available || !this.analysis?.handoff?.canReceivePackages) {
                this.$toast.error("Guided receiving is not available until the uploaded Product Catalog contains every package SKU.")
                return
            }
            const productFile = this.findSelectedFile(this.analysis?.handoff?.productCatalogUploadIndex)
            const stockFile = this.findSelectedFile(this.analysis?.handoff?.packageInventoryUploadIndex)
            setCovaSyncHandoff({ type: "import", productFile, stockFile, fileMode: "separate", warehouseId: this.warehouseId })
            this.$router.push("/cova-import/advanced")
        },
        continueToReconciliation() {
            const stockFile = this.findSelectedFile(this.analysis?.handoff?.packageInventoryUploadIndex)
            setCovaSyncHandoff({ type: "reconciliation", stockFile, warehouseId: this.warehouseId })
            this.$router.push("/cova-reconciliation/advanced")
        },
        fileDescription(file) {
            if (!file.recognized) return "The required Cova headers were not found. This file will not be used."
            return file.roles.map((role) => ROLE_LABELS[role] || role).join(" · ")
        },
        priceSourceLabel(row) {
            return row.priceSource === "inventory_on_hand" ? "Price from Inventory On Hand" : "Price from Product Catalog"
        },
        capabilityClass(item) {
            return { available: item.available, unavailable: !item.available, attention: item.status === "needs_attention" }
        },
        capabilityIcon(item) {
            if (!item.available) return "add_circle_outline"
            if (item.status === "review_only") return "visibility"
            if (item.status === "needs_attention") return "warning_amber"
            return "check_circle"
        },
        capabilityStatus(item) {
            const labels = {
                ready: "Ready",
                up_to_date: "Up to date",
                review_only: "Review only",
                needs_location: "Choose location",
                guided: "Available",
                needs_attention: "Needs review",
                needs_file: "Needs another file",
            }
            return labels[item.status] || item.status
        },
        capabilityStatusClass(item) {
            if (!item.available) return "muted"
            if (["needs_attention", "needs_location"].includes(item.status)) return "warning"
            if (item.status === "review_only") return "info"
            return "good"
        },
        priceStatus(row) {
            return { change: "Will update", unmatched: "New product", ambiguous: "Needs review", missing_price: "No price", conflict: "Conflict" }[row.status] || "No change"
        },
        priceStatusClass(row) {
            return row.status === "change" ? "good" : ["unmatched", "missing_price"].includes(row.status) ? "muted" : "warning"
        },
        highTiesPrice(row) {
            return row.effectiveCurrentPrice === undefined ? row.currentPrice : row.effectiveCurrentPrice
        },
        money(value) {
            if (value === null || value === undefined || value === "") return "—"
            return Number(value).toLocaleString("en-CA", { style: "currency", currency: "CAD" })
        },
        numberFormat(value) {
            return Number(value || 0).toLocaleString()
        },
        signedNumber(value) {
            const number = Number(value || 0)
            return `${number > 0 ? "+" : ""}${number.toLocaleString()}`
        },
        deltaClass(value) {
            const number = Number(value || 0)
            return number > 0 ? "positive" : number < 0 ? "negative" : "neutral"
        },
    },
}
</script>

<style scoped>
.cova-sync-page { display: grid; min-width: 0; gap: 22px; color: #173630; }
.sync-hero { display: flex; min-width: 0; justify-content: space-between; gap: 28px; align-items: flex-start; padding: 28px; border-radius: 18px; background: linear-gradient(135deg, #eef8f2 0%, #f8fbf8 55%, #eff6f4 100%); border: 1px solid #d9ebe1; }
.eyebrow, .step-label { display: block; margin-bottom: 7px; color: #54736a; font-size: 12px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; }
.sync-hero h2, .section-heading h3 { margin: 0; color: #123c32; }
.sync-hero h2 { max-width: 720px; font-size: 30px; line-height: 1.15; }
.sync-hero p, .section-heading p, .capability p, .detected-file p { margin: 8px 0 0; color: #62736e; line-height: 1.55; }
.safety-pill, .count-pill { display: inline-flex; align-items: center; gap: 7px; flex: 0 0 auto; padding: 9px 12px; border-radius: 999px; background: #fff; border: 1px solid #d4e6dd; color: #315d51; font-size: 13px; font-weight: 700; }
.safety-pill .material-icons { font-size: 18px; }
.count-pill.ready { background: #e6f6ec; color: #1e6b45; }
.sync-card { min-width: 0; padding: 24px; border: 1px solid #e1e9e5; border-radius: 16px; background: #fff; box-shadow: 0 8px 24px rgba(25, 57, 49, .04); }
.section-heading { display: flex; justify-content: space-between; gap: 20px; align-items: flex-start; margin-bottom: 20px; }
.section-heading h3 { font-size: 21px; }
.file-drop { display: grid; justify-items: center; gap: 6px; padding: 34px 24px; border: 2px dashed #b9d2c6; border-radius: 14px; background: #fbfdfc; cursor: pointer; transition: .18s ease; }
.file-drop:hover, .file-drop.dragging { border-color: #2d765f; background: #f0f8f4; transform: translateY(-1px); }
.file-drop.disabled { cursor: default; opacity: .65; }
.upload-icon { font-size: 38px; color: #2d765f; }
.file-drop > span:not(.material-icons), .file-drop small { color: #71817c; }
.file-input { display: none; }
.selected-files, .detected-files { display: grid; min-width: 0; gap: 10px; margin-top: 14px; }
.selected-file, .detected-file { display: flex; min-width: 0; align-items: center; gap: 12px; padding: 13px 14px; border: 1px solid #e3ebe7; border-radius: 12px; background: #fcfdfd; }
.selected-file > .material-icons, .detected-file > .material-icons { color: #39745f; }
.selected-file div, .detected-file div { min-width: 0; flex: 1; }
.selected-file strong, .detected-file strong { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.selected-file small { color: #788680; }
.selected-file button { display: inline-grid; place-items: center; padding: 5px; border: 0; border-radius: 50%; background: transparent; color: #65736e; }
.selected-file button:hover { background: #edf3f0; }
.detected-file.unknown { background: #fffaf2; border-color: #f1dfbf; }
.detected-file.unknown > .material-icons { color: #9a6a17; }
.detected-file p { margin-top: 2px; font-size: 13px; }
.action-row { display: flex; align-items: center; gap: 14px; margin-top: 18px; }
.text-button, .inline-action { border: 0; background: transparent; color: #286c57; font-weight: 700; }
.inline-action { display: inline-flex; align-items: center; gap: 5px; padding: 5px 0; }
.inline-action .material-icons { font-size: 17px; }
.capability-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
.capability { display: flex; gap: 14px; padding: 18px; border: 1px solid #e2e9e5; border-radius: 14px; background: #fafcfb; }
.capability.available { background: #f8fcfa; border-color: #d5e8de; }
.capability.unavailable { color: #66746f; }
.capability.attention { border-color: #ebd29d; background: #fffbf3; }
.capability-icon { display: grid; place-items: center; width: 38px; height: 38px; flex: 0 0 38px; border-radius: 10px; background: #e8f3ed; color: #2b7059; }
.capability.unavailable .capability-icon { background: #edf0ef; color: #83908b; }
.capability-copy { min-width: 0; flex: 1; }
.capability-title { display: flex; justify-content: space-between; gap: 10px; align-items: center; }
.capability h4 { margin: 0; color: #24443b; font-size: 16px; }
.capability ul { margin: 10px 0 0; padding-left: 18px; color: #8a651f; font-size: 13px; }
.status-chip { display: inline-flex; align-items: center; padding: 5px 8px; border-radius: 999px; font-size: 11px; font-weight: 700; white-space: nowrap; }
.status-chip.good { background: #e1f4e8; color: #206642; }
.status-chip.warning { background: #fff0ce; color: #81590f; }
.status-chip.info { background: #e6f0f8; color: #315f7a; }
.status-chip.muted { background: #edf0ef; color: #66736f; }
.price-stats { display: flex; gap: 8px; flex-wrap: wrap; justify-content: flex-end; }
.price-stats span { padding: 7px 9px; border-radius: 8px; background: #f1f5f3; color: #60716b; font-size: 12px; }
.table-wrap { overflow-x: auto; border: 1px solid #e3e9e6; border-radius: 12px; }
table { width: 100%; border-collapse: collapse; }
th { padding: 11px 13px; background: #f3f6f5; color: #5c6d67; font-size: 11px; letter-spacing: .04em; text-align: left; text-transform: uppercase; }
td { padding: 12px 13px; border-top: 1px solid #e8eeeb; color: #42544e; font-size: 13px; }
td small { display: block; margin-top: 3px; color: #7b8984; }
.confirmation-panel { display: flex; justify-content: space-between; gap: 20px; align-items: center; margin-top: 18px; padding: 16px; border-radius: 12px; background: #f0f8f4; border: 1px solid #d5e8de; }
.confirmation-panel label { display: flex; gap: 10px; align-items: flex-start; max-width: 720px; color: #36564d; font-weight: 600; }
.confirmation-panel input { margin-top: 3px; }
.notice { display: flex; align-items: flex-start; gap: 10px; margin: 0 0 16px; padding: 13px 14px; border-radius: 10px; }
.notice p { margin: 1px 0 0; }
.warning-notice { background: #fff8e9; color: #7e5c1a; }
.info-notice { background: #eef6fa; color: #3a657a; }
.success-notice { margin-top: 16px; background: #eaf7ef; color: #246746; }
.inventory-heading { align-items: flex-end; }
.location-field { min-width: 250px; }
.location-field label { display: block; margin-bottom: 6px; color: #53655f; font-size: 12px; font-weight: 700; }
.location-field select { width: 100%; height: 40px; padding: 0 10px; border: 1px solid #cfdad5; border-radius: 8px; background: #fff; }
.metric-row { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; margin-bottom: 14px; }
.metric-row div { padding: 13px; border-radius: 10px; background: #f4f7f5; }
.metric-row span { display: block; color: #697872; font-size: 12px; }
.metric-row strong { display: block; margin-top: 4px; color: #284b40; font-size: 20px; }
.positive { color: #236c48; font-weight: 700; }
.negative { color: #a5483e; font-weight: 700; }
.neutral { color: #697872; }
.show-more-row { display: flex; justify-content: center; padding-top: 14px; }
@media (max-width: 900px) {
    .sync-hero, .section-heading, .confirmation-panel, .inventory-heading { flex-direction: column; }
    .capability-grid, .metric-row { grid-template-columns: 1fr; }
    .location-field { width: 100%; }
}
@media (max-width: 600px) {
    .sync-hero, .sync-card { padding: 18px; }
    .sync-hero h2 { font-size: 25px; }
    .section-heading { gap: 12px; }
    .capability { min-width: 0; padding: 14px; }
    .capability-title { align-items: flex-start; flex-direction: column; }
    .capability p, .capability li, .notice p { overflow-wrap: anywhere; }
    .price-stats { justify-content: flex-start; }
    .detected-file { align-items: flex-start; flex-wrap: wrap; }
    .detected-file .status-chip { margin-left: 38px; }
    table { min-width: 620px; }
}
</style>
